// Single source of truth for portfolio content.
// All facts here are drawn from Simon's CV — keep them accurate.

export const profile = {
  name: 'Simon El Khoury El Saifi',
  shortName: 'Simon Saifi',
  initials: 'SS',
  role: 'Full-Stack Developer',
  location: 'Marseille, France',
  email: 'simon.saifi2000@gmail.com',
  phone: '+33 6 22 18 85 23',
  phoneHref: '+33622188523',
  linkedin: 'https://www.linkedin.com/in/simon-el-khoury-el-saifi/',
  github: 'https://github.com/simonsaifi',
  githubHandle: '@simonsaifi',
  cv: 'Simon-Saifi-CV.pdf',
  availability: 'Open to Full-Stack roles (CDI)',
  typed: [
    'Full-Stack Developer',
    'React & Spring Boot',
    'Web & Mobile apps',
    'Docker & CI/CD',
  ],
  lead:
    "Full-Stack Developer based in Marseille with a Master's in Software Engineering. " +
    'I design, build and ship Agile web & mobile apps across the Java, React and PHP ecosystem — ' +
    'currently building internal tools used daily by ~350 agents at CCAS de Marseille.',
  spokenLanguages: [
    { name: 'French', level: 'Fluent' },
    { name: 'English', level: 'Fluent' },
    { name: 'Arabic', level: 'Native' },
  ],
};

export const stats = [
  { value: 350, suffix: '+', label: 'Daily users impacted' },
  { value: 15, suffix: '+', label: 'Technologies shipped' },
  { value: 4, suffix: '', label: 'Companies & teams' },
  { value: 3, suffix: '', label: 'Languages spoken' },
];

// Worked-with strip (text logos — no fabricated brand assets)
export const workedWith = [
  'CCAS de Marseille',
  'ATOS',
  'Grand Port Maritime de Marseille',
  'XMSPRO',
  'Université d’Avignon',
];

export const skillGroups = [
  {
    title: 'Languages',
    icon: 'code',
    items: ['Java', 'JavaScript', 'PHP', 'Python', 'SQL', 'C++'],
  },
  {
    title: 'Frameworks & Libraries',
    icon: 'layers',
    items: ['Spring Boot', 'ReactJS', 'Flutter', 'GWT'],
  },
  {
    title: 'DevOps & Tools',
    icon: 'tool',
    items: ['Docker', 'Git', 'Jenkins', 'CircleCI', 'Maven', 'Jira', 'JBOSS'],
  },
  {
    title: 'Practices',
    icon: 'flow',
    items: ['Agile / Scrum', 'REST APIs', 'CI/CD', 'Testing'],
  },
];

// Projects double as case studies (problem → approach → result).
export const projects = [
  {
    id: 'orbit',
    title: 'Orbit — A Little Home for Two',
    org: 'Personal project',
    tag: 'PWA · Real-time',
    featured: true,
    period: 'Jun 2026 – Present',
    image: null,
    cover: { icon: 'heart', from: '#ec4899', to: '#a855f7' },
    summary:
      'A real-time PWA for couples — shared live status, nudges, a location radar and push notifications, wrapped in a playful “relationship OS” interface.',
    problem:
      'Busy schedules and distance make it hard for two people to stay in sync. I wanted one small, playful app — a shared little home — where you and your person can feel close in real time.',
    approach:
      'Built a mobile-first, installable PWA in React + TypeScript (Vite, Tailwind). Supabase powers realtime sync, anonymous auth and Postgres; two phones pair via a 6-character code. Live location runs on Leaflet, while Supabase Edge Functions + Web Push (VAPID) deliver notifications even when the app is closed. Framer Motion drives the animations; Workbox makes it offline-ready.',
    result:
      'A shippable, installable app with live shared status (mood / energy / hunger), real-time nudges, a location radar, chat, memories, daily questions and a reunion countdown — plus a demo mode so it runs with zero setup.',
    tech: ['React', 'TypeScript', 'Vite', 'Tailwind CSS', 'Supabase', 'Framer Motion', 'Leaflet', 'PWA'],
    links: { live: '', code: 'https://github.com/simonsaifi/sophia' },
    privateNote: 'Personal project — code on request',
  },
  {
    id: 'ccas',
    title: 'Internal Business Apps Suite',
    org: 'CCAS de Marseille',
    tag: 'Full-Stack · Production',
    featured: true,
    period: 'Jun 2025 – Present',
    image: null,
    cover: { icon: 'building', from: '#6366f1', to: '#a855f7' },
    summary:
      'A suite of internal business tools used daily by ~350 agents to run the establishment’s operations.',
    problem:
      'Teams across CCAS de Marseille needed reliable internal tools — mail tracking, training management, a chatbot and a TPE scanner — to run daily operations without friction.',
    approach:
      'Designed, built and maintained the applications end-to-end in Agile: needs analysis, development, testing and production deployment. PHP and ReactJS/JavaScript front-ends backed by SQL.',
    result:
      'Multiple apps (Courrier, Formation, ChatBot, Scanner TPE…) now in daily use by ~350 agents, streamlining everyday workflows across the organisation.',
    tech: ['PHP', 'ReactJS', 'JavaScript', 'SQL', 'Agile'],
    links: { live: '', code: '' },
    privateNote: 'Internal / private project',
  },
  {
    id: 'gpmm',
    title: 'GPMM Web & Mobile Application',
    org: 'ATOS',
    tag: 'Web & Mobile · Enterprise',
    featured: true,
    period: 'Sep 2023 – Jun 2024',
    image: null,
    cover: { icon: 'anchor', from: '#0ea5e9', to: '#6366f1' },
    summary:
      'A cross-platform web & mobile app for the Grand Port Maritime de Marseille, a strategic ATOS client.',
    problem:
      'The Grand Port Maritime de Marseille (GPMM), a strategic client of the group, required a robust cross-platform web and mobile application.',
    approach:
      'Built the front-end interfaces in Flutter and integrated REST web services on a Java / Spring Boot back-end deployed on JBOSS, collaborating in an Agile team against fixed milestones.',
    result:
      'Delivered an enterprise-grade application for a strategic port client, on schedule and to spec.',
    tech: ['Flutter', 'Java', 'Spring Boot', 'REST', 'GWT', 'JBOSS', 'Maven', 'SQL'],
    links: { live: '', code: '' },
    privateNote: 'Enterprise / private project',
  },
  {
    id: 'samaflix',
    title: 'SamaFlix — Google TV Streaming App',
    org: 'XMSPRO',
    tag: 'Android · Streaming',
    featured: false,
    period: 'Feb 2022 – Jul 2022',
    image: null,
    cover: { icon: 'tv', from: '#ec4899', to: '#a855f7' },
    summary:
      'A streaming client (SamaFlix) for Google TV set-top boxes, compliant with platform standards.',
    problem:
      'Deliver a streaming client for Google TV boxes that complies with the platform’s standards and performs well on TV hardware.',
    approach:
      'Developed the Android application in Java, solving complex compatibility and performance challenges specific to the TV form factor.',
    result:
      'Shipped a compliant, performant streaming client (SamaFlix) running on Google TV boxes.',
    tech: ['Java', 'Android', 'Maven'],
    links: { live: '', code: '' },
    privateNote: 'Client / private project',
  },
  {
    id: 'drowsiness',
    title: 'Face Recognition & Drowsiness Detection',
    org: 'Academic — final-year project',
    tag: 'AI / Computer Vision',
    featured: false,
    period: '2022',
    image: 'img/SeniorFinal.PNG',
    summary:
      'A real-time computer-vision system that recognises faces and detects driver drowsiness to help prevent accidents.',
    problem:
      'Drowsy driving causes accidents — a system was needed to detect signs of drowsiness in real time and identify the driver.',
    approach:
      'Built a Python computer-vision pipeline combining face recognition with eye/feature analysis to flag drowsiness as it happens.',
    result:
      'A working prototype demonstrating real-time face recognition and drowsiness alerts, completed as a final-year project.',
    tech: ['Python', 'OpenCV', 'AI / ML'],
    links: { live: '', code: 'https://github.com/simonsaifi/Senior-Final' },
    privateNote: '',
  },
  {
    id: 'lecedrus',
    title: 'Le Cedrus — Restaurant Website',
    org: 'Freelance',
    tag: 'Web · In progress',
    featured: false,
    period: '2024',
    image: 'img/LeCedrus.PNG',
    summary:
      'A Lebanese restaurant website presenting the menu, story and location, with a Firebase-backed content layer.',
    problem:
      'A Lebanese restaurant needed an inviting online presence to showcase its menu, story and location.',
    approach:
      'Built a responsive React site with a Firebase back-end for content, focusing on a warm, appetising visual design.',
    result:
      'A live, mobile-friendly site that presents the restaurant beautifully (ongoing iteration).',
    tech: ['ReactJS', 'Firebase', 'JavaScript', 'Bootstrap'],
    links: { live: 'https://lecedrus.vercel.app', code: '' },
    privateNote: '',
  },
  {
    id: 'portfolio',
    title: 'This Portfolio',
    org: 'Personal',
    tag: 'Web · Open source',
    featured: false,
    period: '2025',
    image: 'img/Portfolio.PNG',
    summary:
      'A fast, animated single-page portfolio built with React — the site you’re looking at right now.',
    problem:
      'Showcase my work and story in a way that feels as polished as the products I build.',
    approach:
      'Designed and built a React single-page app with a custom design system, light/dark theming, scroll-driven motion and case-study modals.',
    result:
      'A performant, accessible portfolio that doubles as a demonstration of front-end craft.',
    tech: ['ReactJS', 'styled-components', 'Framer Motion'],
    links: { live: 'https://simonsaifi.vercel.app', code: 'https://github.com/simonsaifi/Portfolio' },
    privateNote: '',
  },
];

export const timeline = [
  {
    type: 'work',
    title: 'Full-Stack Developer',
    org: 'CCAS de Marseille — Marseille, France',
    date: 'Jun 2025 – Present',
    points: [
      'Design, develop and maintain internal business apps (PHP, ReactJS, JavaScript, SQL) for the establishment’s teams.',
      'Own the full development cycle in Agile: needs analysis, development, testing and production.',
      'Internal apps in daily use by ~350 agents (Courrier, Formation, ChatBot, Scanner TPE…).',
    ],
  },
  {
    type: 'work',
    title: 'Apprentice Full-Stack Developer',
    org: 'ATOS — Aix-en-Provence, France',
    date: 'Sep 2023 – Jun 2024',
    points: [
      'Built a web & mobile app for the Grand Port Maritime de Marseille (GPMM), a strategic client.',
      'Flutter front-ends + Java / Spring Boot REST services deployed on JBOSS.',
      'Agile delivery against fixed milestones. Stack: Java, Spring Boot, Flutter, Maven, JavaScript, GWT, JBOSS, SQL.',
    ],
  },
  {
    type: 'work',
    title: 'Android Developer',
    org: 'XMSPRO — Jounieh, Lebanon',
    date: 'Feb 2022 – Jul 2022',
    points: [
      'Developed a streaming app (SamaFlix client) for Google TV boxes, compliant with platform standards.',
      'Solved complex compatibility and performance challenges. Stack: Java, Maven.',
    ],
  },
  {
    type: 'work',
    title: 'IT Intern',
    org: 'AUL — Jounieh, Lebanon',
    date: 'Sep 2021 – Feb 2022',
    points: [
      'Maintained and diagnosed the university’s IT systems and servers.',
      'Administered network connections and provided user support (Cisco routers & switches).',
    ],
  },
  {
    type: 'education',
    title: "Master’s — Software Engineering for the Digital Society",
    org: 'CERI, Université d’Avignon — France',
    date: '2022 – 2024',
    points: [
      'Key courses: full-stack web/mobile development, DevOps, Artificial Intelligence, digital project management.',
      'Vice-President of Projects & Events — BDE CERI Avignon.',
    ],
  },
  {
    type: 'education',
    title: "Bachelor’s — Computer Science",
    org: 'AUL — Jounieh, Lebanon',
    date: '2018 – 2022',
    points: ['Foundations in software development, algorithms and data structures.'],
  },
];
